import StaticPage from "../components/StaticPage";
import useSEO from "../hooks/useSEO";

export default function TermsPage() {
  useSEO({
    title: "Terms and Conditions | GetDeparto",
    description:
      "Read the terms and conditions for using GetDeparto's temporary flight reservation service — including service scope, validity, refunds, and acceptable use.",
    canonical: "https://getdeparto.com/terms",
    og: {
      title: "Terms and Conditions | GetDeparto",
      description:
        "Read the terms and conditions for using GetDeparto's temporary flight reservation service.",
      url: "https://getdeparto.com/terms",
      image: "https://getdeparto.com/og-image.png",
      type: "website",
      siteName: "GetDeparto",
    },
  });
  return (
    <StaticPage
      title="Terms and Conditions"
      subtitle="Please read these terms carefully before using getdeparto. By placing an order, you agree to everything set out below."
      updated="April 2026"
    >
      <section>
        <h2>1. Nature of the service</h2>
        <ul>
          <li>
            getdeparto (operated by Get Departo) provides{" "}
            <strong>temporary flight reservations</strong> with a live PNR,
            intended for visa applications, proof of onward travel, and general
            travel planning.
          </li>
          <li>
            Our output is <strong>not a ticketed booking</strong>. It is a
            held reservation that cannot be used to board a flight.
          </li>
          <li>
            Validity of each reservation depends on airline and global
            distribution systems and the plan selected at checkout.
          </li>
        </ul>
      </section>

      <section>
        <h2>2. No guarantee</h2>
        <ul>
          <li>
            We do not guarantee visa approval. Visa outcomes are decided solely
            by embassies or consulates.
          </li>
          <li>
            We do not guarantee that any specific embassy, consulate, airline,
            or immigration officer will accept a reservation. Documentation
            requirements vary by country and change over time.
          </li>
        </ul>
      </section>

      <section>
        <h2>3. Customer responsibility</h2>
        <ul>
          <li>
            You must provide your full name exactly as shown on your passport
            and the travel details you actually intend to use.
          </li>
          <li>
            Incorrect names, wrong travel dates, invalid email or WhatsApp
            numbers, and similar customer-side errors are your responsibility
            and are not grounds for a refund.
          </li>
        </ul>
      </section>

      <section>
        <h2>4. Delivery</h2>
        <ul>
          <li>
            Reservations are delivered as a PDF itinerary via WhatsApp and
            email to the contact details you provide.
          </li>
          <li>
            We aim to deliver within the timeframe stated at checkout
            (typically within 60 minutes). Occasional delays may occur due to
            airline systems, payment verification, or operational reasons.
          </li>
        </ul>
      </section>

      <section>
        <h2>5. Validity</h2>
        <ul>
          <li>
            Each reservation is valid only for the window stated on your order
            (for example, 5 days for flight reservations, 10 days for visa-application
            reservations, hotel reservations, and bundles).
          </li>
          <li>
            After the validity window expires, the PNR may no longer be
            retrievable on the airline system. This is expected behaviour.
          </li>
        </ul>
      </section>

      <section>
        <h2>6. Verification</h2>
        <ul>
          <li>
            During the validity window, the PNR may be verifiable on the
            airline's own website or booking-lookup system.
          </li>
          <li>
            Verification availability depends on the airline's system status,
            policies, and any changes they make outside our control.
          </li>
        </ul>
      </section>

      <section>
        <h2>7. Refund and reissue policy</h2>
        <ul>
          <li>
            If the PNR is not verifiable within the promised validity window,
            or the reservation is not delivered at all, we will{" "}
            <strong>reissue</strong> the reservation or{" "}
            <strong>refund</strong> your payment.
          </li>
          <li>
            Refunds are not available for: reservations whose validity has
            expired, orders placed with incorrect details supplied by the
            customer, visa rejection or delay by any embassy, or any outcome
            outside our control.
          </li>
          <li>
            Refund requests must be raised during the validity window via
            WhatsApp or email to the contact below. Approved refunds are
            processed to the original payment method within 5–10 business days.
          </li>
        </ul>
      </section>

      <section>
        <h2>8. Limitation of liability</h2>
        <ul>
          <li>
            Our liability, in any circumstance, is limited to the amount you
            paid for the relevant reservation.
          </li>
          <li>
            We are not responsible for visa decisions, embassy or airline
            policies, missed travel, flight cancellations, currency losses, or
            other consequential or indirect damages.
          </li>
        </ul>
      </section>

      <section>
        <h2>9. Acceptable use</h2>
        <ul>
          <li>
            You agree not to misuse our service for fraudulent purposes.
          </li>
          <li>
            You agree not to modify, forge, or alter any document we deliver.
            Altered documents lose all authenticity and may constitute a legal
            offence.
          </li>
        </ul>
      </section>

      <section>
        <h2>10. Modifications to these terms</h2>
        <p>
          We may update these Terms from time to time. The latest version will
          always be published on this page with an updated date. Continued use
          of the service after changes constitutes acceptance of the revised
          terms.
        </p>
      </section>

      <section>
        <h2>11. Governing law and jurisdiction</h2>
        <p>
          These Terms are governed by the laws of India. Any dispute arising
          from the use of getdeparto is subject to the exclusive jurisdiction
          of the courts at Bengaluru, India.
        </p>
      </section>

      <section>
        <h2>12. Contact</h2>
        <p>
          Get Departo · Bengaluru, India
          <br />
          WhatsApp:{" "}
          <a className="gd-link" href="https://wa.me/919036705707">
            +91 90367 05707
          </a>
          <br />
          Email:{" "}
          <a className="gd-link" href="mailto:support@getdeparto.com">
            support@getdeparto.com
          </a>
        </p>
      </section>
    </StaticPage>
  );
}
